import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { cloneDeep } from 'lodash';
import { Observable, TimeoutError, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { IPayload } from 'src/app/interface/filters';
import { environment } from 'src/environments/environment';
import { ApplicationService } from './application.service';
import { AuthService } from './auth.service';
import { MyUtils } from './my-utils.service';

export interface ChatSessionResponse {
  mcpSessionId?: string;
  sessionId?: string;
  session_id?: string;
  id?: string;
  data?: {
    mcpSessionId?: string;
    sessionId?: string;
    session_id?: string;
    id?: string;
    [key: string]: unknown;
  };
  message?: string | string[];
  reply?: string;
  response?: string;
  text?: string;
  messages?: ChatHistoryMessage[];
  [key: string]: unknown;
}

export interface ChatHistoryMessage {
  content?: string;
  role?: string;
  text?: string;
  message?: string;
  sender?: string;
}

export interface ChatRequestMessage {
  role: string;
  content: string;
}

export interface OrchestratorRequest {
  filter: {
    network: IPayload['network'];
    period: IPayload['period'];
    question: IPayload['question'];
    vehicle: IPayload['vehicle'];
    additionalParameters?: IPayload['additionalParameters'];
  };
  usecase: string;
  endValidationDate: string;
}

export interface ChatRequest {
  messages: ChatRequestMessage[];
  request: OrchestratorRequest;
}

export interface ChatResponse {
  message?: string | string[];
  token?: string;
  mcpSessionId?: string;
  session_id?: string;
  reply?: string;
  response?: string;
  text?: string;
  data?: {
    mcpSessionId?: string;
    [key: string]: unknown;
  };
  [key: string]: unknown;
}

@Injectable({
  providedIn: 'root',
})
export class ChatbotService {
  private endpoint = environment.csibefrontend + 'ask-roar/';
  private readonly mcpSessionStorageKey = 'chatbotMcpSessionId';
  private readonly mcpSessionUserKey = 'chatbotMcpSessionUser';

  constructor(
    private http: HttpClient,
    private utils: MyUtils,
    private applicationService: ApplicationService,
    private authService: AuthService
  ) {
    this.authService.token$.subscribe((token) => {
      if (!token) {
        this.clearMcpSession();
      }
    });
  }

  buildSessionPayload(): IPayload {
    const payload: IPayload = cloneDeep(this.utils.settingsPayloadValue.payload);

    payload.question = {
      ...payload.question,
      locale: this.applicationService.languageSelectValue,
      section: this.applicationService.navBarSelectValue?.idItem ?? payload.question?.section ?? '',
      tab: this.applicationService.navBarSelectValue?.idItem ?? payload.question?.tab ?? '',
    };

    return payload;
  }

  buildOrchestratorRequest(): OrchestratorRequest {
    const payload = this.buildSessionPayload();

    return {
      filter: {
        network: payload.network,
        period: payload.period,
        question: payload.question,
        vehicle: payload.vehicle,
        additionalParameters: payload.additionalParameters,
      },
      usecase: this.applicationService.navBarSelectValue?.idItem ?? payload.question?.section ?? '',
      endValidationDate: payload.period?.current?.to ?? '',
    };
  }

  getStoredMcpSessionId(): string | null {
    const storedUser = localStorage.getItem(this.mcpSessionUserKey);
    const currentUser = this.getCurrentUserIdentifier();

    if (!storedUser || storedUser !== currentUser) {
      if (storedUser) {
        this.clearMcpSession();
      }
      return null;
    }

    const sessionId = localStorage.getItem(this.mcpSessionStorageKey);
    return sessionId?.trim() ? sessionId.trim() : null;
  }

  storeMcpSessionId(sessionId: string): void {
    localStorage.setItem(this.mcpSessionStorageKey, sessionId);
    localStorage.setItem(this.mcpSessionUserKey, this.getCurrentUserIdentifier());
  }

  clearMcpSession(): void {
    localStorage.removeItem(this.mcpSessionStorageKey);
    localStorage.removeItem(this.mcpSessionUserKey);
  }

  createSession(filters: IPayload): Observable<ChatSessionResponse> {
    return this.http
      .post<ChatSessionResponse | string>(this.endpoint + 'session', filters, {
        observe: 'response',
      })
      .pipe(
        timeout(60000),
        map((response) => this.normalizeSessionResponse(response)),
        catchError((error) =>
          throwError(() =>
            error instanceof TimeoutError
              ? new Error('Chat session timed out')
              : error
          )
        )
      );
  }

  sendMessage(
    mcpSessionId: string,
    history: Array<{ text: string; sender: 'user' | 'bot' }>
  ): Observable<ChatResponse> {
    const body: ChatRequest = {
      messages: history.map((entry) => ({
        role: entry.sender === 'user' ? 'user' : 'assistant',
        content: entry.text,
      })),
      request: this.buildOrchestratorRequest(),
    };

    return this.http
      .post<ChatResponse>(this.endpoint + 'chat', body, {
        headers: new HttpHeaders({
          'Mcp-Session-Id': mcpSessionId,
        }),
        observe: 'response',
      })
      .pipe(
        timeout(120000),
        map((response) => {
          const payload = (response.body ?? {}) as ChatResponse;
          const headerId = this.readMcpSessionHeader(response.headers);
          if (headerId && !payload.mcpSessionId) {
            return { ...payload, mcpSessionId: headerId };
          }
          return payload;
        }),
        catchError((error) =>
          throwError(() =>
            error instanceof TimeoutError
              ? new Error('Chat request timed out')
              : error
          )
        )
      );
  }

  extractMcpSessionId(
    response: ChatSessionResponse | ChatResponse | string
  ): string | null {
    if (typeof response === 'string') {
      return response.trim().length > 0 ? response.trim() : null;
    }

    const candidates = [
      response.mcpSessionId,
      response.data?.mcpSessionId,
    ];

    for (const candidate of candidates) {
      if (typeof candidate === 'string' && candidate.trim().length > 0) {
        return candidate.trim();
      }
    }

    return null;
  }

  private normalizeSessionResponse(
    response: HttpResponse<ChatSessionResponse | string>
  ): ChatSessionResponse {
    const headerId = this.readMcpSessionHeader(response.headers);
    const body = response.body;

    if (typeof body === 'string') {
      return {
        mcpSessionId: headerId ?? (body.trim() || undefined),
        message: body.trim() || undefined,
      };
    }

    const payload = (body ?? {}) as ChatSessionResponse;
    const mcpSessionId =
      this.extractMcpSessionId(payload) ?? headerId ?? undefined;

    return mcpSessionId ? { ...payload, mcpSessionId } : payload;
  }

  private readMcpSessionHeader(headers: HttpHeaders): string | null {
    const value =
      headers.get('Mcp-Session-Id') ??
      headers.get('mcp-session-id') ??
      headers.get('MCP-Session-Id');
    return value?.trim() ? value.trim() : null;
  }

  extractBotMessages(response: ChatSessionResponse | ChatResponse): string[] {
    const text = this.extractBotText(response);
    return text ? [text] : [];
  }

  extractBotText(response: ChatSessionResponse | ChatResponse): string | null {
    const { message, reply, response: responseText, text } = response;

    if (Array.isArray(message)) {
      const merged = message
        .filter((item): item is string => typeof item === 'string')
        .join('');
      return merged.length > 0 ? merged : null;
    }

    const single =
      (typeof message === 'string' ? message : null) ??
      reply ??
      responseText ??
      text;

    if (typeof single === 'string' && single.length > 0) {
      return single;
    }

    return null;
  }

  private getCurrentUserIdentifier(): string {
    return this.authService.email || localStorage.getItem('email') || '';
  }
}
